import React, { useEffect, useState } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Smartphone, Monitor, Database, Cog, Brain, GitBranch, Award, Zap } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { certifications, awards } from '../data/mockData';
import { trackButtonClick } from '../utils/trackButtonClick';

// Award Card Component - Simple card without confetti
const AwardCard = ({ award, isDark, text, isMobile = false }) => {
  const [cardRef] = useInView({
    threshold: 0.5,
    triggerOnce: true
  });

  if (isMobile) {
    return (
      <div
        ref={cardRef}
        className={`relative p-4 ${isDark ? 'bg-white/5 border-white/10 active:bg-white/15' : 'bg-gray-50 border-gray-200 active:bg-gray-100'} rounded-xl border transition-all duration-300 group/award overflow-hidden`}
      >
        <div className="flex flex-col gap-0.5">
          {/* Award Name with Icon */}
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <div className={`w-8 h-8 rounded-lg ${isDark ? 'bg-yellow-500/10' : 'bg-yellow-50'} flex items-center justify-center`}>
                <Award className="w-4 h-4 text-yellow-500" />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className={`${text.primary} group-hover/award:text-yellow-500 transition-colors font-semibold text-sm leading-tight`}>
                {award.name}
              </div>
            </div>
          </div>
          
          {/* Company and Date - Aligned with Award Title (after icon) */}
          <div className="flex items-center justify-between gap-4 min-w-0 ml-11">
            <span className={`text-xs ${text.muted} truncate flex-1`}>
              {award.company}
            </span>
            <span className="text-yellow-500 font-medium text-xs whitespace-nowrap flex-shrink-0">
              {award.month} {award.year}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={cardRef}
      className={`relative p-5 ${isDark ? 'bg-white/5 border-white/10 hover:bg-white/10' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'} rounded-xl border transition-all duration-300 group/award hover:shadow-lg overflow-hidden`}
    >
      <div className="flex flex-col gap-3">
        {/* Award Name with Icon */}
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <div className={`w-10 h-10 rounded-lg ${isDark ? 'bg-yellow-500/10' : 'bg-yellow-50'} flex items-center justify-center group-hover/award:scale-110 transition-transform duration-300`}>
              <Award className="w-5 h-5 text-yellow-500" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className={`${text.primary} group-hover/award:text-yellow-500 transition-colors font-semibold text-base leading-tight`}>
              {award.name}
            </div>
          </div>
        </div>
        
        {/* Company and Date - Aligned with Icon */}
        <div className="flex items-center justify-between gap-4 min-w-0">
          <span className={`text-sm ${text.muted} truncate flex-1`}>
            {award.company}
          </span>
          <span className="text-yellow-500 font-medium text-xs whitespace-nowrap flex-shrink-0">
            {award.month} {award.year}
          </span>
        </div>
      </div>
    </div>
  );
};

const SkillsSection = () => {
  const [showAllCertifications, setShowAllCertifications] = useState(false);
  const [showAllAwards, setShowAllAwards] = useState(false);
  const { isDark, background, text, accents } = useTheme();
  const controls = useAnimation();
  const [sectionRef, inView] = useInView({
    threshold: 0,
    triggerOnce: true
  });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
      return;
    }
    // Safety net: if the scroll-triggered observer never fires (can happen
    // on mobile with fast flick scrolling past a viewport-height section),
    // reveal the section anyway after a short delay instead of staying blank.
    const fallback = setTimeout(() => controls.start('visible'), 1500);
    return () => clearTimeout(fallback);
  }, [controls, inView]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        duration: 0.6
      }
    }
  };

  const skillBarVariants = {
    hidden: { width: '0%' },
    visible: (level) => ({
      width: `${level}%`,
      transition: {
        duration: 2,
        ease: "easeOut"
      }
    })
  };

  const skillCategories = [
    {
      icon: Smartphone,
      title: "Mobile Development",
      level: 95,
      skills: ["Swift", "SwiftUI", "Objective-C", "Xcode", "iOS SDK", "macOS", "Core Data", "UIKit"],
      color: "from-blue-400 to-blue-600",
      glow: "shadow-blue-500/30"
    },
    {
      icon: Monitor,
      title: "Architecture & Patterns",
      level: 92,
      skills: ["MVC", "MVVM", "MVP", "VIPER", "SOLID", "Clean Architecture", "Design Patterns"],
      color: "from-green-400 to-green-600",
      glow: "shadow-green-500/30"
    },
    {
      icon: Brain,
      title: "AI/ML & Advanced",
      level: 60,
      skills: ["Core ML", "NLP", "CreateML", "AI Integration", "Claude Code", "GitHub Copilot", "Prompt Engineering"],
      color: "from-purple-400 to-purple-600",
      glow: "shadow-purple-500/30"
    },
    {
      icon: Database,
      title: "Data & Performance",
      level: 90,
      skills: ["Core Data", "SQLite", "SwiftData", "JSON", "REST APIs", "Memory Management", "Performance Tuning"],
      color: "from-orange-500 to-red-600",
      glow: "shadow-orange-600/40"
    },
    {
      icon: Cog,
      title: "DevOps & Tools",
      level: 85,
      skills: ["Xcode", "Instruments", "JIRA", "TestFlight", "App Store Connect", "Gitlab", "Github", "CI/CD"],
      color: "from-red-400 to-red-600",
      glow: "shadow-red-500/30"
    },
    {
      icon: GitBranch,
      title: "Leadership & Process",
      level: 94,
      skills: ["Agile", "Scrum", "Team Leadership", "Mentoring", "Code Reviews", "Technical Documentation"],
      color: "from-indigo-400 to-indigo-600",
      glow: "shadow-indigo-500/30"
    }
  ];

  return (
    <motion.section 
      id="skills" 
      ref={sectionRef} 
      className={`min-h-screen py-20 ${background.section} relative overflow-hidden transition-all duration-500`}
      initial="hidden"
      animate={controls}
      variants={containerVariants}
    >
      {/* Floating Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {Array.from({ length: 20 }).map((_, i) => (
          <div
            key={i}
            className={`absolute w-2 h-2 bg-blue-400 rounded-full ${isDark ? 'opacity-20' : 'opacity-30'} animate-ping`}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 2}s`
            }}
          ></div>
        ))}
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className={`inline-block px-4 py-2 ${background.card} rounded-full ${accents.skills} font-mono text-sm mb-6`}>
              &gt; skills --all
            </div>
            <h2 className={`text-5xl lg:text-7xl font-bold ${text.gradient} mb-6`}>
              Expertise
            </h2>
            <p className={`text-xl ${text.secondary} max-w-2xl mx-auto`}>
              Comprehensive technical skills spanning the entire iOS ecosystem and beyond
            </p>
          </div>

          {/* Skills Grid */}
          <div className="grid lg:grid-cols-2 gap-8 mb-16">
            {skillCategories.map((category, index) => {
              const Icon = category.icon;
              return (
                <div
                  key={index}
                  className="group relative"
                >
                  <div className={`relative ${background.card} rounded-2xl p-8 shadow-2xl ${category.glow} hover:scale-105 transition-all duration-500 overflow-hidden`}>
                    {/* Background Glow */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${category.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500 rounded-2xl`}></div>
                    
                    {/* Header */}
                    <div className="flex items-center justify-between mb-6 relative z-10">
                      <div className="flex items-center space-x-4">
                        <div className={`p-3 bg-gradient-to-br ${category.color} rounded-xl ${category.glow}`}>
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        <h3 className={`text-xl font-bold ${text.primary}`}>
                          {category.title}
                        </h3>
                      </div>
                      <div className={`text-2xl font-bold ${text.muted}`}>
                        {category.level}%
                      </div>
                    </div>

                    {/* Skill Level Bar */}
                    <div className="mb-6 relative z-10">
                      <div className={`w-full h-2 ${isDark ? 'bg-gray-800' : 'bg-gray-300'} rounded-full overflow-hidden`}>
                        <motion.div
                          className={`h-full bg-gradient-to-r ${category.color} rounded-full ${category.glow}`}
                          variants={skillBarVariants}
                          custom={category.level}
                          initial="hidden"
                          animate={inView ? "visible" : "hidden"}
                          transition={{
                            duration: 2,
                            delay: index * 0.3,
                            ease: "easeOut"
                          }}
                        />
                      </div>
                      {/* Animated percentage display */}
                      <motion.div 
                        className="mt-2 text-right"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: inView ? 1 : 0 }}
                        transition={{ delay: index * 0.3 + 1 }}
                      >
                        <span className={`text-sm font-mono ${text.muted}`}>
                          {inView && (
                            <motion.span
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ delay: index * 0.3 + 1.5 }}
                            >
                              {category.level}%
                            </motion.span>
                          )}
                        </span>
                      </motion.div>
                    </div>

                    {/* Skills Tags */}
                    <div className="flex flex-wrap gap-2 relative z-10">
                      {category.skills.map((skill, skillIndex) => (
                        <span
                          key={skillIndex}
                          className={`px-3 py-1 ${isDark ? 'bg-white/10 text-gray-300 border-white/20 hover:bg-white/20 hover:text-white' : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200 hover:text-gray-900'} text-sm rounded-full border transition-all duration-300`}
                          style={{
                            animationDelay: `${skillIndex * 100}ms`
                          }}
                        >
                          {skill}
                        </span>
                      ))}
                    </div>

                    {/* Floating Icon */}
                    <div className={`absolute -top-2 -right-2 ${isDark ? 'opacity-5' : 'opacity-10'} group-hover:opacity-10 transition-opacity duration-500`}>
                      <Icon className={`w-20 h-20 ${text.muted}`} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Achievements Section - Responsive: Expandable (Mobile) + Grid (Desktop) */}
          <div className="grid md:grid-cols-2 gap-8">
            {/* Certifications */}
            <div className="relative">
              <div className={`${background.card} rounded-2xl p-8 shadow-2xl hover:shadow-green-500/10 transition-all duration-500 group`}>
                <div className="flex items-center space-x-3 mb-6">
                  <Award className="w-8 h-8 text-green-400" />
                  <h3 className={`text-2xl font-bold ${text.primary}`}>Certifications</h3>
                </div>

                {/* Mobile: Expandable Layout */}
                <div className="md:hidden space-y-4">
                  {certifications.slice(0, showAllCertifications ? certifications.length : 3).map((cert, index) => (
                    <div
                      key={index}
                      className={`p-4 ${isDark ? 'bg-white/5 border-white/10 hover:bg-white/10' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'} rounded-xl border transition-all duration-300 group/cert`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center space-x-3">
                          <span className="text-xl">{cert.icon}</span>
                          <span className={`${text.secondary} group-hover/cert:${text.primary} transition-colors font-medium text-sm`}>
                            {cert.name}
                          </span>
                        </div>
                        <span className="text-green-400 font-mono text-xs whitespace-nowrap ml-4">
                          {cert.year}
                        </span>
                      </div>
                      <div className={`text-xs ${text.muted} italic ml-8`}>
                        {cert.company}
                      </div>
                    </div>
                  ))}
                  {/* Show More/Less Button - Mobile Only */}
                  {certifications.length > 3 && (
                    <button
                      onClick={() => {
                        trackButtonClick({
                          section: 'skills',
                          label: showAllCertifications ? 'Show Less Certifications' : 'Show More Certifications',
                          type: 'content_toggle'
                        });
                        setShowAllCertifications(!showAllCertifications);
                      }}
                      className={`w-full mt-4 px-4 py-2 ${isDark ? 'bg-green-500/10 hover:bg-green-500/20 text-green-400 border-green-500/30' : 'bg-green-50 hover:bg-green-100 text-green-600 border-green-200'} border rounded-lg transition-all duration-300 text-sm font-medium`}
                    >
                      {showAllCertifications ? 'Show Less' : `Show ${certifications.length - 3} More`}
                    </button>
                  )}
                </div>

                {/* Desktop: Grid Layout */}
                <div className="hidden md:grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {certifications.map((cert, index) => (
                    <div
                      key={index}
                      className={`p-4 ${isDark ? 'bg-white/5 border-white/10 hover:bg-white/10' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'} rounded-xl border transition-all duration-300 group/cert`}
                    >
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-lg">{cert.icon}</span>
                        <div className={`${text.secondary} group-hover/cert:${text.primary} transition-colors font-medium text-sm flex-1`}>
                          {cert.name}
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className={`text-xs ${text.muted} italic`}>
                          {cert.company}
                        </div>
                        <span className="text-green-400 font-mono text-xs font-bold">
                          {cert.year}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Awards */}
            <div className="relative">
              <div className={`${background.card} rounded-2xl p-8 shadow-2xl hover:shadow-yellow-500/10 transition-all duration-500 group`}>
                <div className="flex items-center space-x-3 mb-6">
                  <Zap className="w-8 h-8 text-yellow-400" />
                  <h3 className={`text-2xl font-bold ${text.primary}`}>Awards</h3>
                </div>

                {/* Mobile: Expandable Layout */}
                <div className="md:hidden space-y-3">
                  {awards.slice(0, showAllAwards ? awards.length : 4).map((award, index) => (
                    <AwardCard 
                      key={index} 
                      award={award} 
                      isDark={isDark} 
                      text={text} 
                      isMobile={true} 
                    />
                  ))}
                  {/* Show More/Less Button - Mobile Only */}
                  {awards.length > 4 && (
                    <button
                      onClick={() => {
                        trackButtonClick({
                          section: 'skills',
                          label: showAllAwards ? 'Show Less Awards' : 'Show More Awards',
                          type: 'content_toggle'
                        });
                        setShowAllAwards(!showAllAwards);
                      }}
                      className={`w-full mt-4 px-4 py-2 ${isDark ? 'bg-yellow-500/10 hover:bg-yellow-500/20 text-yellow-400 border-yellow-500/30' : 'bg-yellow-50 hover:bg-yellow-100 text-yellow-600 border-yellow-200'} border rounded-lg transition-all duration-300 text-sm font-medium`}
                    >
                      {showAllAwards ? 'Show Less' : `Show ${awards.length - 4} More`}
                    </button>
                  )}
                </div>

                {/* Desktop: Grid Layout */}
                <div className="hidden md:grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {awards.map((award, index) => (
                    <AwardCard 
                      key={index} 
                      award={award} 
                      isDark={isDark} 
                      text={text} 
                      isMobile={false} 
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.section>
  );
};

export default SkillsSection;